import React from "react";

/*
 * Mission section — why ZelTechnology exists, who we serve, and who we lift up.
 * Used on the Home page; a compact variant powers the LAMNA page.
 */

const pillars = [
  {
    icon: (
      // heart-in-hands style icon
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
    ),
    title: "Helping people, not charging them",
    text: "Technology should lift people up — not leave them behind because of a price tag. We provide IT help, guidance, and protection for the people who need it most, completely free.",
    accent: "#12bfbe",
  },
  {
    icon: (
      // shield
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
    ),
    title: "A safe harbor in a confusing digital world",
    text: "Scams, data breaches, and predatory tech support target the most vulnerable first. We stand between them and the people they prey on — with plain language, honest advice, and real protection.",
    accent: "#9198e5",
  },
  {
    icon: (
      // users/team
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
    ),
    title: "Built by those who served",
    text: "Our team is home to like-minded individuals who have served our country — people who know what duty means. Here, service continues: veterans and mission-driven technologists building something that gives back.",
    accent: "#1e3a8a",
  },
  {
    icon: (
      // lightbulb
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
    ),
    title: "Philanthropy is the business model",
    text: "Every project, every repair, every line of code flows from one goal: helping anyone who needs it. The digital age can be overwhelming — when our community grows, our ability to guide people through it grows with it.",
    accent: "#36395a",
  },
];

export function MissionSection({ compact = false }) {
  return (
    <div
      id="mission"
      className="w-full py-12 lg:py-20"
      style={{ background: "linear-gradient(160deg, #f0fbfb 0%, #eef0fb 100%)" }}
    >
      <div className="mx-4 lg:mx-20 max-w-6xl mx-auto">
        <div className="text-center mb-10" data-aos="zoom-in">
          <h2 className="text-3xl md:text-4xl font-extrabold text-blue-900 mb-3">
            Why ZelTechnology Exists
          </h2>
          <p className="text-gray-600 max-w-3xl mx-auto">
            Because technology moves fast — and everyone deserves a patient,
            honest guide through it. No matter your age, income, or
            background, help should never come with a predator's price tag.
          </p>
        </div>

        <div
          className={`grid gap-6 ${compact ? "grid-cols-1 sm:grid-cols-2" : "grid-cols-1 md:grid-cols-2"}`}
        >
          {pillars.map((p) => (
            <div
              key={p.title}
              className="bg-white rounded-2xl p-6 shadow-xl border-t-4 flex gap-4"
              style={{ borderTopColor: p.accent }}
              data-aos="fade-up"
            >
              <div
                className="flex-shrink-0 w-12 h-12 rounded-xl flex items-center justify-center text-white shadow-md"
                style={{ backgroundColor: p.accent }}
              >
                <svg
                  className="w-6 h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                >
                  {p.icon}
                </svg>
              </div>
              <div>
                <h3 className="font-bold text-lg text-blue-900 mb-1">
                  {p.title}
                </h3>
                <p className="text-gray-600 text-sm leading-relaxed">{p.text}</p>
              </div>
            </div>
          ))}
        </div>

        {!compact && (
          <div
            className="mt-10 text-center rounded-2xl p-8 shadow-xl text-white"
            style={{ background: "linear-gradient(90deg, #36395a, #1e3a8a)" }}
            data-aos="fade-up"
          >
            <p className="text-lg md:text-xl font-semibold max-w-3xl mx-auto leading-relaxed">
              "Technology, infrastructure, cybersecurity, and artificial
              intelligence are tools supporting the mission —{" "}
              <span className="text-teal-300">
                the mission is people.
              </span>
              "
            </p>
            <p className="text-gray-300 mt-3 text-sm">
              — The ZelTechnology guiding philosophy
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

const Mission = () => <MissionSection />;

export default Mission;
